import WarningCard from 'components/WarningCard';
import React, { FC, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import {
  getFriendlyErrorMessageIfErrorOccurred,
  getPrettyDate,
} from 'utils/util/util';
import FeedbackRequestCard from 'views/Feedback/FeedbackRequestCard';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import { FeedbackApiResponse } from '../types';

type Props = {
  isOpen: boolean;
  feedbackRequestId: number;
  onClosed: () => void;
  onDelete: (feedbackRequest: any) => void;
};
const DeleteModal: FC<Props> = ({
  isOpen,
  feedbackRequestId,
  onClosed,
  onDelete,
}) => {
  const proxyParameters = useProxyParameters();
  const { formatMessage, locale } = useIntl();
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );

  const { data, error, status } = useConfirmApi<FeedbackApiResponse>({
    url: `/admin/feedback-requests/${feedbackRequestId}`,
    method: 'GET',
    disabled: !isOpen,
    clearOnChange: true,
    clearOnError: true,
  });

  const handleOnDeleteCallback = useCallback(() => {
    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `/admin/feedback-requests/${feedbackRequestId}`,
      proxyParameters,
      (data, error: unknown, hardErrorMessage: unknown) => {
        if (error || hardErrorMessage) {
          setErrorMessage(
            getFriendlyErrorMessageIfErrorOccurred(error, hardErrorMessage)
          );
          return;
        }
        onDelete(data);
        onClosed();
      }
    );
  }, [feedbackRequestId, onClosed, onDelete, proxyParameters]);

  useEffect(() => {
    if (status === 'ERROR') {
      // @ts-expect-error
      if (error?.status === 404) {
        toast.warn(
          formatMessage({
            id: 'app.views.administration.content_administration.feedback_and_recognition_admin.requested.delete_modal.error.feedback_not_owned',
            defaultMessage:
              'You do not have permission to delete this feedback request. It may have already been deleted or you may have lost the ownership, since belongs to people you may not have admin capability against anymore.',
          })
        );
      } else {
        toast.error(
          formatMessage({
            id: 'app.views.administration.content_administration.feedback_and_recognition_admin.requested.delete_modal.error.fetching_feedback_data',
            defaultMessage: 'Error fetching feedback request data',
          })
        );
      }
    }
  }, [error, formatMessage, status]);

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.feedback_and_recognition_admin.requested.delete_modal.message.fetching_feedback_data',
          defaultMessage: 'Fetching feedback request data',
        })}
      />
    );
  }

  return (
    <>
      {!!data && (
        <ConfirmationDialogModal
          isOpen={isOpen}
          onClosed={onClosed}
          toggle={onClosed}
          confirmCallback={handleOnDeleteCallback}
          title={formatMessage(
            {
              id: 'app.views.administration.content_administration.feedback_and_recognition_admin.requested.delete_modal.title.delete_feedback',
              defaultMessage:
                'Delete feedback request from {personFullName} made on {date}',
            },
            {
              // @ts-expect-error
              personFullName: data.author_person.full_name,
              // @ts-expect-error
              date: getPrettyDate({ dateString: data.created_at, locale }),
            }
          )}
          description={
            <div>
              <div className="mb-4">
                {formatMessage({
                  id: 'app.views.administration.content_administration.feedback_and_recognition_admin.requested.delete_modal.description.delete_feedback',
                  defaultMessage:
                    'Are you sure that you want to remove this feedback request?',
                })}
              </div>
              {/* @ts-expect-error */}
              {data.feedback?.length > 0 && (
                <WarningCard
                  message={formatMessage(
                    {
                      id: 'app.views.administration.content_administration.feedback_and_recognition_admin.requested.delete_modal.description.delete_feedback_warning',
                      defaultMessage:
                        '{count, plural, one {There is # feedback item} other {There are # feedback items}} associated with this request. If you delete it, all the feedback will still remain, but the request will be removed. If you want to delete the feedback items as well, you will have to do it manually using the <link>Feedback & recognition</link> content administration page.',
                    },
                    {
                      link: (chunks: React.ReactNode) => (
                        <a
                          href="/admin/feedback-recognition"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </a>
                      ),
                      // @ts-expect-error
                      count: data.feedback.length,
                    }
                  )}
                />
              )}
              <FeedbackRequestCard feedbackRequest={data} isPreview={true} />
            </div>
          }
          confirmText={formatMessage({
            id: 'app.views.administration.content_administration.feedback_and_recognition_admin.requested.delete_modal.confirm_text.delete',
            defaultMessage: 'Delete',
          })}
          validationErrors={errorMessage}
        />
      )}
    </>
  );
};

export default React.memo(DeleteModal);
